import axios from 'axios';
import { Product, Category, PaginatedResponse, ProductFilters, Order, User, AuthTokens, LoginCredentials, RegisterData, CustomerProfile, FooterSection, Review, ReviewFormData } from '../types';

// Use environment variable, fallback to /api for development proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add language header to all requests
api.interceptors.request.use((config) => {
  const language = localStorage.getItem('language') || 'bn';
  config.headers['Accept-Language'] = language;

  // Add JWT token if available
  let token = localStorage.getItem('token');

  // Fallback: Check authTokens if token is missing
  if (!token) {
    const authTokens = localStorage.getItem('authTokens');
    if (authTokens) {
      try {
        const parsed = JSON.parse(authTokens);
        token = parsed.access;
      } catch (e) {
        console.error('Failed to parse authTokens in interceptor', e);
      }
    }
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

// ============================================
// AUTO-REFRESH TOKEN ON 401 RESPONSES
// ============================================
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors, skip if already retried or if it's a refresh/login request
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/register')
    ) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Get refresh token from localStorage
      const storedTokens = localStorage.getItem('authTokens');
      if (!storedTokens) {
        throw new Error('No stored tokens');
      }

      const parsedTokens = JSON.parse(storedTokens);
      if (!parsedTokens.refresh) {
        throw new Error('No refresh token');
      }

      // Call the refresh endpoint
      const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
        refresh: parsedTokens.refresh,
      });

      const newAccessToken = response.data.access;
      // If backend rotates refresh tokens, update it too
      const newRefreshToken = response.data.refresh || parsedTokens.refresh;

      // Update localStorage
      const updatedTokens = {
        access: newAccessToken,
        refresh: newRefreshToken,
      };
      localStorage.setItem('authTokens', JSON.stringify(updatedTokens));
      localStorage.setItem('token', newAccessToken);

      // Process queued requests with new token
      processQueue(null, newAccessToken);

      // Retry original request
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed — token is truly expired/invalid, force logout
      processQueue(refreshError, null);
      localStorage.removeItem('authTokens');
      localStorage.removeItem('token');

      // Redirect to login page
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/login';
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Product API functions
export const productAPI = {
  // Get all products with optional filters
  getProducts: async (filters?: ProductFilters): Promise<PaginatedResponse<Product>> => {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.price_min) params.append('price_min', filters.price_min.toString());
      if (filters.price_max) params.append('price_max', filters.price_max.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.in_stock) params.append('in_stock', 'true');
      if (filters.ordering) params.append('ordering', filters.ordering);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.page_size) params.append('page_size', filters.page_size.toString());
    }

    const response = await api.get<PaginatedResponse<Product>>(`/products/?${params.toString()}`);
    return response.data;
  },

  // Get single product by slug
  getProductBySlug: async (slug: string): Promise<Product> => {
    const response = await api.get<Product>(`/products/${slug}/`);
    return response.data;
  },

  // Get popular products
  getPopularProducts: async (limit: number = 8): Promise<Product[]> => {
    const response = await api.get<Product[]>(`/products/popular/?limit=${limit}`);
    return response.data;
  },

  // Get products by category
  getProductsByCategory: async (categorySlug: string, page: number = 1): Promise<PaginatedResponse<Product>> => {
    const response = await api.get<PaginatedResponse<Product>>(`/products/by-category/${categorySlug}/?page=${page}`);
    return response.data;
  },
};

// Category API functions
export const categoryAPI = {
  // Get all categories
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get<Category[]>('/categories/');
    return response.data;
  },

  // Get single category by slug
  getCategoryBySlug: async (slug: string): Promise<Category> => {
    const response = await api.get<Category>(`/categories/${slug}/`);
    return response.data;
  },
};

// Order API functions
export const orderAPI = {
  // Create new order
  createOrder: async (orderData: any): Promise<Order> => {
    const response = await api.post<Order>('/orders/', orderData);
    return response.data;
  },

  // Get user's order history (requires authentication)
  getOrderHistory: async (): Promise<Order[]> => {
    const response = await api.get<Order[]>('/orders/');
    return response.data;
  },

  // Get order details (requires authentication)
  getOrderDetails: async (orderId: number): Promise<Order> => {
    const response = await api.get<Order>(`/orders/${orderId}/`);
    return response.data;
  },

  // Track order by order number (public)
  trackOrder: async (orderNumber: string): Promise<Order> => {
    const response = await api.get<Order>(`/orders/track/${orderNumber}/`);
    return response.data;
  },

  // PreOrder methods for auto-save
  createPreOrder: async (data: any): Promise<Order> => {
    const response = await api.post<Order>('/orders/preorder/', data);
    return response.data;
  },

  updatePreOrder: async (id: number, data: any): Promise<Order> => {
    const response = await api.patch<Order>(`/orders/${id}/preorder/`, data);
    return response.data;
  },

  confirmPreOrder: async (id: number): Promise<Order> => {
    const response = await api.post<Order>(`/orders/${id}/preorder/confirm/`);
    return response.data;
  },
};

// Authentication API functions
export const authAPI = {
  // Register new user
  register: async (data: RegisterData): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post('/auth/register/', data);
    return response.data;
  },

  // Login
  login: async (credentials: LoginCredentials): Promise<AuthTokens> => {
    const response = await api.post('/auth/login/', credentials);
    return response.data;
  },

  // Refresh token
  refreshToken: async (refresh: string): Promise<{ access: string }> => {
    const response = await api.post('/auth/refresh/', { refresh });
    return response.data;
  },

  // Logout
  logout: async (refreshToken: string): Promise<void> => {
    await api.post('/auth/logout/', { refresh_token: refreshToken });
  },

  // Get user profile
  getProfile: async (): Promise<CustomerProfile> => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  // Update profile
  updateProfile: async (data: Partial<CustomerProfile>): Promise<CustomerProfile> => {
    const response = await api.put('/auth/profile/', data);
    return response.data;
  },

  // Update profile with picture (FormData)
  updateProfileWithPicture: async (formData: FormData): Promise<CustomerProfile> => {
    const response = await api.put('/auth/profile/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Change password
  changePassword: async (oldPassword: string, newPassword: string, newPassword2: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/change-password/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password2: newPassword2,
    });
    return response.data;
  },

  // Get order history
  getOrderHistory: async (): Promise<Order[]> => {
    const response = await api.get('/auth/orders/');
    // Handle pagination (if response has results property, use that, otherwise use response data)
    if (response.data && (response.data as any).results) {
      return (response.data as any).results;
    }
    return Array.isArray(response.data) ? response.data : [];
  },
};

// Layout/Content API functions
export const getFooter = async (): Promise<{ data: FooterSection[] }> => {
  // If there is a real endpoint:
  return {
    data: [
      {
        id: 1,
        title_bn: 'আমাদের সম্পর্কে',
        title_en: 'About Us',
        content_bn: '<p>আমরা আপনার বিশ্বস্ত অনলাইন শপিং পার্টনার।</p>',
        content_en: '<p>We are your trusted online shopping partner.</p>'
      },
      {
        id: 2,
        title_bn: 'গ্রাহক সেবা',
        title_en: 'Customer Service',
        content_bn: '<ul><li>সহায়তা কেন্দ্র</li><li>রিটার্ন পলিসি</li></ul>',
        content_en: '<ul><li>Help Center</li><li>Return Policy</li></ul>'
      }
    ]
  };
};

// Banner API functions
export const bannerAPI = {
  getActiveBanners: async (): Promise<any[]> => {
    const response = await api.get('/banners/active/');
    return response.data;
  }
};

export const getActiveBanners = bannerAPI.getActiveBanners;

// Review API functions (customer-facing)
export const reviewAPI = {
  // Get approved reviews for a product
  getProductReviews: async (productId: number): Promise<Review[]> => {
    const response = await api.get<Review[]>(`/reviews/?product=${productId}`);
    return response.data;
  },

  // Create a new review (requires authentication)
  createReview: async (data: ReviewFormData): Promise<Review> => {
    if (data.image) {
      const formData = new FormData();
      formData.append('product', data.product.toString());
      formData.append('rating', data.rating.toString());
      formData.append('comment', data.comment);
      formData.append('image', data.image);

      const response = await api.post<Review>('/reviews/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await api.post<Review>('/reviews/', data);
    return response.data;
  },
};

export const getProducts = productAPI.getProducts;
export const getCategories = categoryAPI.getCategories;
export const getPopularProducts = productAPI.getPopularProducts;
export const getProductBySlug = productAPI.getProductBySlug;

// Content API functions
export const contentAPI = {
  // Get active delivery options
  getDeliveryOptions: async () => {
    const response = await api.get('/delivery-options/');
    return response.data;
  },

  // Get active banners
  getBanners: async () => {
    const response = await api.get('/banners/');
    return response.data;
  },

  // Get home page content (announcement, features)
  getHomePageContent: async () => {
    const response = await api.get('/settings/homepage/');
    return response.data;
  },
};

// Admin API functions
export const adminAPI = {
  // Get dashboard stats
  getDashboardStats: async (): Promise<any> => {
    const response = await api.get('/admin/dashboard/stats/');
    return response.data;
  },

  // Get dashboard chart data with optional date range
  getDashboardCharts: async (startDate?: string, endDate?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/admin/dashboard/charts/', { params });
    return response.data;
  },


  // Products management
  getProducts: async () => api.get('/admin/products/'),
  createProduct: async (data: any) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.post('/admin/products/', data, config);
  },
  updateProduct: async (id: number, data: any) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.put(`/admin/products/${id}/`, data, config);
  },
  deleteProduct: async (id: number) => api.delete(`/admin/products/${id}/`),

  // Product Images management
  uploadProductImages: async (productId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    return api.post(`/admin/products/${productId}/upload_images/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteProductImage: async (productId: number, imageId: number) => {
    return api.delete(`/admin/products/${productId}/images/${imageId}/`);
  },
  getProductImages: async (productId: number) => {
    return api.get(`/admin/products/${productId}/`);
  },

  // Categories management
  getCategories: async () => api.get('/admin/categories/'),
  createCategory: async (data: any) => api.post('/admin/categories/', data),
  updateCategory: async (id: number, data: any) => api.put(`/admin/categories/${id}/`, data),
  deleteCategory: async (id: number) => api.delete(`/admin/categories/${id}/`),

  // Orders management (admin view)
  getOrders: async () => api.get('/orders/'),
  updateOrderStatus: async (id: number, status: string) => api.patch(`/orders/${id}/update_status/`, { status }),
  updateOrder: async (id: number, data: any) => api.patch(`/orders/${id}/`, data),
  createManualOrder: async (data: any) => api.post('/orders/create_manual/', data),
  applyOrderDiscount: async (id: number, discountType: string | null, discountValue: number | null) =>
    api.post(`/orders/${id}/apply_discount/`, { discount_type: discountType, discount_value: discountValue }),
  
  // Steadfast Courier integration
  sendOrderToSteadfast: async (orderId: number) => api.post(`/orders/${orderId}/send_to_steadfast/`),
  getSteadfastTracking: async (orderId: number) => api.get(`/orders/${orderId}/steadfast_tracking/`),
  bulkSendToSteadfast: async (orderIds: number[]) => api.post('/orders/bulk_send_to_steadfast/', { order_ids: orderIds }),
  bulkPickupRequest: async (orderIds: number[]) => api.post('/orders/bulk_pickup_request/', { order_ids: orderIds }),
  getCustomerSteadfastHistory: async (phone: string) => api.get(`/orders/customer_steadfast_history/?phone=${encodeURIComponent(phone)}`),
  syncSteadfastStatuses: async (orderIds?: number[]) => api.post('/orders/sync_steadfast_statuses/', orderIds ? { order_ids: orderIds } : {}),

  // ── Fulfillment: Pick & Pack, Labels, Weight ──
  getPickPackList: async (orderIds: number[]) => api.post('/orders/pick_pack_list/', { order_ids: orderIds }),
  getPickPackPDF: async (orderIds: number[]) => api.post('/orders/pick_pack_pdf/', { order_ids: orderIds }, { responseType: 'blob' }),
  generateLabels: async (orderIds: number[], senderInfo?: any) => api.post('/orders/generate_labels/', { order_ids: orderIds, sender_info: senderInfo }, { responseType: 'blob' }),
  calculateWeight: async (data: { actual_weight: number; length: number; width: number; height: number; zone_code?: string; order_id?: number }) => api.post('/orders/calculate_weight/', data),
  getShippingZones: async () => api.get('/orders/shipping_zones/'),
  saveShippingZone: async (data: any) => api.post('/orders/save_shipping_zone/', data),
  deleteShippingZone: async (id: number) => api.post('/orders/delete_shipping_zone/', { id }),

  // Customers management
  getCustomers: async () => api.get('/customers/'),

  // Reviews management
  getReviews: async () => api.get('/reviews/'),
  approveReview: async (id: number) => api.post(`/reviews/${id}/approve/`),
  rejectReview: async (id: number) => api.post(`/reviews/${id}/reject/`),
  deleteReview: async (id: number) => api.delete(`/reviews/${id}/`),

  // Coupons management
  getCoupons: async () => api.get('/coupons/'),
  createCoupon: async (data: any) => api.post('/coupons/', data),
  updateCoupon: async (id: number, data: any) => api.put(`/coupons/${id}/`, data),
  deleteCoupon: async (id: number) => api.delete(`/coupons/${id}/`),



  // Delivery Options management
  getDeliveryOptions: async () => api.get('/admin/delivery-options/'),
  createDeliveryOption: async (data: any) => api.post('/admin/delivery-options/', data),
  updateDeliveryOption: async (id: number, data: any) => api.put(`/admin/delivery-options/${id}/`, data),
  deleteDeliveryOption: async (id: number) => api.delete(`/admin/delivery-options/${id}/`),

  // Content & SEO management
  getSEO: async () => api.get('/admin/seo/'),
  updateSEO: async (data: any) => api.post('/admin/seo/', data),
  getContent: async () => api.get('/admin/content/'), // Navbar/Footer
  updateContent: async (type: string, data: any) => api.post(`/admin/content/${type}/`, data),

  // Integrations & Pixel
  getIntegrations: async () => api.get('/admin/integrations/'),
  createIntegration: async (data: any) => api.post('/admin/integrations/', data),
  updateIntegration: async (id: number, data: any) => api.put(`/admin/integrations/${id}/`, data),
  deleteIntegration: async (id: number) => api.delete(`/admin/integrations/${id}/`),
  getSteadfastPoliceStations: async () => api.get('/admin/integrations/steadfast_police_stations/'),
  getFacebookPixel: async () => api.get('/admin/facebook-pixel/'),
  createFacebookPixel: async (data: any) => api.post('/admin/facebook-pixel/', data),
  updateFacebookPixel: async (id: number, data: any) => api.put(`/admin/facebook-pixel/${id}/`, data),
  deleteFacebookPixel: async (id: number) => api.delete(`/admin/facebook-pixel/${id}/`),

  // Facebook CAPI (Conversion API) management
  getFacebookCAPI: async () => api.get('/admin/facebook-capi/'),
  createFacebookCAPI: async (data: any) => api.post('/admin/facebook-capi/', data),
  updateFacebookCAPI: async (id: number, data: any) => api.put(`/admin/facebook-capi/${id}/`, data),
  deleteFacebookCAPI: async (id: number) => api.delete(`/admin/facebook-capi/${id}/`),
  testFacebookCAPI: async (id: number) => api.post(`/admin/facebook-capi/${id}/test_connection/`),

  // Google Tag Manager management
  getGTM: async () => api.get('/admin/google-tag-manager/'),
  createGTM: async (data: any) => api.post('/admin/google-tag-manager/', data),
  updateGTM: async (id: number, data: any) => api.put(`/admin/google-tag-manager/${id}/`, data),
  deleteGTM: async (id: number) => api.delete(`/admin/google-tag-manager/${id}/`),

  // Meta Ads Configuration
  getMetaAdsConfig: async () => api.get('/admin/meta-ads-config/'),
  createMetaAdsConfig: async (data: any) => api.post('/admin/meta-ads-config/', data),
  updateMetaAdsConfig: async (id: number, data: any) => api.put(`/admin/meta-ads-config/${id}/`, data),
  deleteMetaAdsConfig: async (id: number) => api.delete(`/admin/meta-ads-config/${id}/`),
  testMetaAdsConnection: async (id: number) => api.post(`/admin/meta-ads-config/${id}/test_connection/`),

  // Meta Ads Campaigns & Insights
  getMetaAdsCampaigns: async (datePreset: string = 'last_7d') => api.get(`/admin/meta-ads/campaigns/?date_preset=${datePreset}`),
  getMetaAdsCampaignInsights: async (campaignId: string, datePreset: string = 'last_7d') => api.get(`/admin/meta-ads/campaigns/${campaignId}/insights/?date_preset=${datePreset}`),

  // Campaign Products (product-to-campaign links)
  getCampaignProducts: async () => api.get('/admin/campaign-products/'),
  createCampaignProduct: async (data: any) => api.post('/admin/campaign-products/', data),
  deleteCampaignProduct: async (id: number) => api.delete(`/admin/campaign-products/${id}/`),
  searchCampaignProducts: async (query: string) => api.get(`/admin/campaign-products/search_products/?q=${encodeURIComponent(query)}`),

  // TikTok Pixel management
  getTikTokPixel: async () => api.get('/admin/tiktok-pixel/'),
  createTikTokPixel: async (data: any) => api.post('/admin/tiktok-pixel/', data),
  updateTikTokPixel: async (id: number, data: any) => api.put(`/admin/tiktok-pixel/${id}/`, data),
  deleteTikTokPixel: async (id: number) => api.delete(`/admin/tiktok-pixel/${id}/`),

  // TikTok Events API management
  getTikTokEventsAPI: async () => api.get('/admin/tiktok-events-api/'),
  createTikTokEventsAPI: async (data: any) => api.post('/admin/tiktok-events-api/', data),
  updateTikTokEventsAPI: async (id: number, data: any) => api.put(`/admin/tiktok-events-api/${id}/`, data),
  deleteTikTokEventsAPI: async (id: number) => api.delete(`/admin/tiktok-events-api/${id}/`),
  testTikTokEventsAPI: async (id: number) => api.post(`/admin/tiktok-events-api/${id}/test_connection/`),
  // Banners management
  getBanners: () => api.get('/admin/banners/'),
  createBanner: (data: any) => api.post('/admin/banners/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateBanner: (id: number, data: any) => api.put(`/admin/banners/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteBanner: (id: number) => api.delete(`/admin/banners/${id}/`),

  // Translations
  getTranslations: async () => api.get('/admin/translations/'),
  updateTranslation: async (id: number, data: any) => api.put(`/admin/translations/${id}/`, data),
  importTranslations: async (data: any) => api.post('/admin/translations/import/', data),
  exportTranslations: async () => api.get('/admin/translations/export/'),
};

// Public Access APIs
export const publicAPI = {
  // Get active Facebook Pixel (no auth required)
  getActiveFacebookPixel: async () => {
    const response = await api.get('/integrations/facebook-pixel/active/');
    return response.data;
  },

  // Get active GTM configuration (no auth required)
  getActiveGTM: async () => {
    const response = await api.get('/integrations/gtm/active/');
    return response.data;
  },

  // Get active TikTok Pixel (no auth required)
  getActiveTikTokPixel: async () => {
    const response = await api.get('/integrations/tiktok-pixel/active/');
    return response.data;
  },
};

// Website Settings API
export const websiteSettingsAPI = {
  getSettings: async () => {
    const response = await api.get('/settings/website/');
    return response.data;
  },
};

export default api;
